import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import type { FunnelABTestDecisionRow } from '../types';
import { describeMetric, resolveDecisionVerdict } from '../decisionVerdict';
import { DECISION_SCORECARD_TEST_IDS, STAT_TIER_TEST_IDS } from '../testIds';
import { StatTierChip } from './StatTierChip';

/**
 * R3-3 (D-Q3/B4): decision actions against the experiments-registry
 * `transition` endpoint — records a decision, never moves traffic. The dialog
 * enforces rationale client-side (server requires it for BOTH actions, PE-3);
 * illegal-transition problem+json renders inline, never toast-only.
 */
export interface DecisionScorecardActions {
  /** Registry experiment state (shown as context in the dialog). */
  experimentState?: string;
  onTransition: (
    action: 'declare_winner' | 'stop',
    payload: { rationale: string; winnerVariantId?: string },
  ) => Promise<void>;
}

interface DecisionScorecardProps {
  row: FunnelABTestDecisionRow;
  /** Optional navigation to the variant's web placement. */
  onOpenVariant?: (variantId: string) => void;
  /** R3-3: present only when the row maps to a registry experiment. */
  actions?: DecisionScorecardActions;
}

const VERDICT_COLOR: Record<string, 'success' | 'error' | 'default'> = {
  ship: 'success',
  dont_ship: 'error',
  keep_collecting: 'default',
};

function formatPercent(value: number | null): string {
  return value === null ? 'n/a' : `${Math.round(value * 100)}%`;
}

/** R3-1: probabilities cap at «>99%» — never a dishonest «100%». */
function formatProbability(value: number | null): string {
  if (value === null) return 'n/a';
  if (value >= 0.995) return '>99%';
  return `${Math.round(value * 100)}%`;
}

/** R3-1 significance coloring: green ≥95%, red ≤5%, gray in between. */
function probabilityColor(value: number | null): string {
  if (value === null) return 'text.secondary';
  if (value >= 0.95) return 'success.main';
  if (value <= 0.05) return 'error.main';
  return 'text.secondary';
}

function formatMoney(value: number | null): string {
  return value === null ? 'n/a' : `$${value.toFixed(2)}`;
}

const PRIMARY_METRIC_VALUE: Record<string, (score: { firstToSecondRate: number | null; purchaseConversionRate: number | null; revenuePerAssignedUser: number | null; profitPerAssignedUser: number | null }) => string> = {
  first_to_second_rate: score => formatPercent(score.firstToSecondRate),
  purchase_conversion_rate: score => formatPercent(score.purchaseConversionRate),
  revenue_per_assigned_user: score => formatMoney(score.revenuePerAssignedUser),
  profit_per_assigned_user: score => formatMoney(score.profitPerAssignedUser),
};

/** R3-1: inline 95%-CI bar for Δ vs control — data-true interval rendering. */
function CiBar({ interval, domainMax }: { interval: [number, number] | null; domainMax: number }): React.ReactElement | null {
  if (!interval || domainMax <= 0) return null;
  const [lo, hi] = interval;
  const clamp = (value: number) => Math.max(-domainMax, Math.min(domainMax, value));
  const leftPct = ((clamp(lo) + domainMax) / (2 * domainMax)) * 100;
  const widthPct = Math.max(2, ((clamp(hi) - clamp(lo)) / (2 * domainMax)) * 100);
  const tone = lo > 0 ? 'success.main' : hi < 0 ? 'error.main' : 'text.disabled';
  return (
    <Box sx={{ position: 'relative', height: 6, borderRadius: 3, bgcolor: 'action.hover', minWidth: 72, mt: 0.5 }}>
      <Box sx={{ position: 'absolute', left: '50%', top: -1, bottom: -1, width: '1px', bgcolor: 'divider' }} />
      <Box sx={{ position: 'absolute', left: `${leftPct}%`, width: `${widthPct}%`, top: 0, bottom: 0, borderRadius: 3, bgcolor: tone }} />
    </Box>
  );
}

/**
 * Decision-first A/B scorecard: the verdict and one-line rationale lead;
 * statistics live behind the Details expander (progressive disclosure).
 */
export const DecisionScorecard: React.FC<DecisionScorecardProps> = ({ row, onOpenVariant, actions }) => {
  const [expanded, setExpanded] = useState(false);
  const [pendingAction, setPendingAction] = useState<'declare_winner' | 'stop' | null>(null);
  const [rationale, setRationale] = useState('');
  const [selectedWinnerId, setSelectedWinnerId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [recordedAction, setRecordedAction] = useState<'declare_winner' | 'stop' | 'keep' | null>(null);
  const verdict = resolveDecisionVerdict(row);
  const splitBroken = verdict.sampleRatioMismatch;
  const allocationUnknown = verdict.allocationMetadataUnknown;
  const decisionBlocked = verdict.stopValve || verdict.deliveryOnly;

  const openActionDialog = (action: 'declare_winner' | 'stop') => {
    setPendingAction(action);
    setRationale('');
    setActionError(null);
    // Default winner: the recorded winner, else the best P(beat control) non-control variant.
    const best = [...row.scores]
      .filter(score => score.variantId !== row.controlVariantId)
      .sort((a, b) => (b.comparisonToControl[row.primaryMetric]?.probabilityToBeatControl ?? -1)
        - (a.comparisonToControl[row.primaryMetric]?.probabilityToBeatControl ?? -1))[0];
    setSelectedWinnerId(row.winnerVariantId ?? best?.variantId ?? '');
  };

  const submitAction = async () => {
    if (!actions || !pendingAction) return;
    setSubmitting(true);
    setActionError(null);
    try {
      await actions.onTransition(pendingAction, {
        rationale: rationale.trim(),
        ...(pendingAction === 'declare_winner' ? { winnerVariantId: selectedWinnerId } : {}),
      });
      setRecordedAction(pendingAction);
      setPendingAction(null);
    } catch (error) {
      // Illegal-transition problem+json (and any other failure) renders INLINE.
      setActionError(error instanceof Error ? error.message : String(error));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Paper
      variant="outlined"
      sx={{ p: 1.5, ...(splitBroken && { borderColor: 'error.main' }) }}
      data-testid={DECISION_SCORECARD_TEST_IDS.card(row.testId, row.iterationId)}
    >
      <Typography variant="subtitle2" sx={{ mb: 0.75 }} data-testid={DECISION_SCORECARD_TEST_IDS.title}>
        {row.testName ?? `Test ${row.testId}`}
        <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.75 }}>
          {row.iterationName ?? `iteration ${row.iterationId}`}
        </Typography>
      </Typography>
      {splitBroken && (
        <Alert severity="error" sx={{ mb: 1 }} data-testid={STAT_TIER_TEST_IDS.srmBanner}>
          Traffic split is broken — the numbers below are not comparable. Fix the
          assignment/measurement first; no winner can be declared and follow-up actions are paused.
        </Alert>
      )}
      {allocationUnknown && (
        <Alert severity="warning" sx={{ mb: 1 }} data-testid={STAT_TIER_TEST_IDS.srmBanner}>
          Allocation metadata is unavailable — the numbers below cannot be used for a winner decision.
          The traffic split has not been classified as broken; select one verified allocation epoch or repair its snapshot.
        </Alert>
      )}
      <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
        <Chip
          label={verdict.label}
          color={VERDICT_COLOR[verdict.verdict]}
          data-testid={DECISION_SCORECARD_TEST_IDS.verdict}
          sx={{ fontWeight: 600 }}
        />
        <Typography variant="body2" sx={{ flex: 1, minWidth: 240 }} data-testid={DECISION_SCORECARD_TEST_IDS.reason}>
          {verdict.reason}
        </Typography>
        {row.minSampleTarget !== undefined && row.minSampleProgress !== undefined && (
          <Typography variant="caption" color="text.secondary">
            {Math.round(row.minSampleProgress * 100)}% of min sample ({row.minSampleTarget.toLocaleString('en-US')}/variant)
          </Typography>
        )}
        <Stack direction="row" spacing={2}>
          <Box data-testid={DECISION_SCORECARD_TEST_IDS.probability}>
            <Typography variant="caption" color="text.secondary" display="block">
              Chance to beat control
            </Typography>
            <Typography variant="body2" fontWeight={600} sx={{ color: probabilityColor(verdict.probabilityToBeatControl) }}>
              {formatProbability(verdict.probabilityToBeatControl)}
            </Typography>
          </Box>
          <Box data-testid={DECISION_SCORECARD_TEST_IDS.expectedLoss}>
            <Typography variant="caption" color="text.secondary" display="block">
              Expected loss / user
            </Typography>
            <Typography variant="body2" fontWeight={600}>
              {formatMoney(verdict.expectedLossPerUser)}
            </Typography>
          </Box>
        </Stack>
        <Button
          size="small"
          data-testid={DECISION_SCORECARD_TEST_IDS.detailsToggle}
          endIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          onClick={() => setExpanded(current => !current)}
        >
          Details
        </Button>
      </Stack>

      {actions && (recordedAction ? (
        <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 1 }} data-testid={DECISION_SCORECARD_TEST_IDS.decisionRecorded}>
          {recordedAction === 'keep'
            ? 'Keeping collecting — no transition recorded.'
            : recordedAction === 'declare_winner'
              ? 'Winner declared — decision recorded in the experiments registry.'
              : 'Test stopped — decision recorded in the experiments registry.'}
        </Typography>
      ) : (
        <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
          <Button
            size="small"
            variant="contained"
            disabled={decisionBlocked}
            onClick={() => openActionDialog('declare_winner')}
            data-testid={DECISION_SCORECARD_TEST_IDS.declareWinnerButton}
          >
            Declare winner
          </Button>
          <Button
            size="small"
            variant="outlined"
            onClick={() => openActionDialog('stop')}
            data-testid={DECISION_SCORECARD_TEST_IDS.stopTestButton}
          >
            Stop test
          </Button>
          <Button
            size="small"
            onClick={() => setRecordedAction('keep')}
            data-testid={DECISION_SCORECARD_TEST_IDS.keepCollectingButton}
          >
            Keep collecting
          </Button>
          <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
            Records a decision in the registry — traffic does not move.
          </Typography>
        </Stack>
      ))}

      {actions && (
        <Dialog open={pendingAction !== null} onClose={() => !submitting && setPendingAction(null)} fullWidth maxWidth="sm" data-testid={DECISION_SCORECARD_TEST_IDS.decisionDialog}>
          <DialogTitle>
            {pendingAction === 'declare_winner' ? 'Declare winner' : 'Stop test'}
            <Typography variant="caption" color="text.secondary" component="div">
              {row.testName ?? row.testId} · {row.iterationName ?? row.iterationId}
              {actions.experimentState ? ` · registry state: ${actions.experimentState}` : ''}
            </Typography>
          </DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 0.5 }}>
              {pendingAction === 'declare_winner' && (
                <TextField
                  select
                  label="Winning variant"
                  value={selectedWinnerId}
                  onChange={event => setSelectedWinnerId(event.target.value)}
                  data-testid={DECISION_SCORECARD_TEST_IDS.decisionWinnerSelect}
                  size="small"
                >
                  {row.scores.map(score => (
                    <MenuItem key={score.variantId} value={score.variantId}>
                      {score.variantName ?? score.variantId}
                      {score.variantId === row.controlVariantId ? ' (control)' : ''}
                    </MenuItem>
                  ))}
                </TextField>
              )}
              <TextField
                label="Rationale (required)"
                value={rationale}
                onChange={event => setRationale(event.target.value)}
                multiline
                minRows={2}
                size="small"
                inputProps={{ 'data-testid': DECISION_SCORECARD_TEST_IDS.decisionRationale }}
                helperText="Recorded in the registry audit trail with your decision."
              />
              {actionError && (
                <Alert severity="error" data-testid={DECISION_SCORECARD_TEST_IDS.decisionError}>
                  {actionError}
                </Alert>
              )}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setPendingAction(null)} disabled={submitting}>Cancel</Button>
            <Button
              variant="contained"
              onClick={submitAction}
              disabled={submitting || rationale.trim().length === 0 || (pendingAction === 'declare_winner' && !selectedWinnerId)}
              data-testid={DECISION_SCORECARD_TEST_IDS.decisionConfirm}
            >
              {pendingAction === 'declare_winner' ? 'Declare winner' : 'Stop test'}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      <Collapse in={expanded}>
        <Divider sx={{ my: 1.5 }} />
        <Box data-testid={DECISION_SCORECARD_TEST_IDS.details}>
          <Typography variant="caption" color="text.secondary">
            Test {row.testId} · iteration {row.iterationId} · primary metric: {describeMetric(row.primaryMetric)}
          </Typography>

          <Stack direction="row" spacing={3} sx={{ mt: 1, mb: 1.5 }}>
            <Box data-testid={DECISION_SCORECARD_TEST_IDS.srm}>
              <Typography variant="caption" color="text.secondary" display="block">
                Sample ratio mismatch p-value
              </Typography>
              <Typography variant="body2" color={verdict.sampleRatioMismatch ? 'error' : 'text.primary'}>
                {row.sampleRatioMismatchPValue === null ? 'n/a' : row.sampleRatioMismatchPValue.toExponential(2)}
                {verdict.sampleRatioMismatch ? ' — mismatch!' : ''}
              </Typography>
            </Box>
            <Box data-testid={DECISION_SCORECARD_TEST_IDS.guardrails}>
              <Typography variant="caption" color="text.secondary" display="block">
                Guardrails
              </Typography>
              {row.scores.flatMap(score => score.guardrails).length === 0 ? (
                <Typography variant="body2">none configured</Typography>
              ) : (
                row.scores.flatMap(score =>
                  score.guardrails.map((guardrail, index) => (
                    <Typography
                      key={`${score.variantId}-${guardrail.metric}-${index}`}
                      variant="body2"
                      color={guardrail.breached ? 'error' : 'text.primary'}
                    >
                      {score.variantId}: {guardrail.metric} — {guardrail.breached ? `BREACHED${guardrail.reason ? ` (${guardrail.reason})` : ''}` : 'ok'}
                    </Typography>
                  )),
                )
              )}
            </Box>
            {row.dataQualityFlags.length > 0 && (
              <Box>
                <Typography variant="caption" color="text.secondary" display="block">
                  Data quality flags
                </Typography>
                <Typography variant="body2" color="warning.main">
                  {row.dataQualityFlags.join(', ')}
                </Typography>
              </Box>
            )}
          </Stack>

          {(() => {
            // R3-1: primary metric leads; CI bars share one symmetric domain.
            const ciDomainMax = Math.max(
              0,
              ...row.scores
                .map(score => score.comparisonToControl[row.primaryMetric]?.confidenceInterval95)
                .filter((interval): interval is [number, number] => Array.isArray(interval))
                .flatMap(([lo, hi]) => [Math.abs(lo), Math.abs(hi)]),
            );
            const secondaryColumns = ([
              ['first_to_second_rate', 'First→second'],
              ['purchase_conversion_rate', 'Purchase conv.'],
              ['revenue_per_assigned_user', 'Revenue / user'],
            ] as const).filter(([key]) => key !== row.primaryMetric);
            return (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Variant</TableCell>
                <TableCell align="right">Users</TableCell>
                <TableCell align="right">{describeMetric(row.primaryMetric)}</TableCell>
                <TableCell align="right">Δ vs control (95% CI)</TableCell>
                <TableCell align="right">P(beat control)</TableCell>
                <TableCell align="right">Expected loss</TableCell>
                {secondaryColumns.map(([key, label]) => (
                  <TableCell key={key} align="right">{label}</TableCell>
                ))}
                <TableCell align="right">Evidence</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {row.scores.map(score => {
                const comparison = score.comparisonToControl[row.primaryMetric];
                const isControl = score.variantId === row.controlVariantId;
                const isWinner = score.variantId === row.winnerVariantId;
                return (
                  <TableRow key={score.variantId} data-testid={DECISION_SCORECARD_TEST_IDS.variantRow(score.variantId)}>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        {onOpenVariant ? (
                          <Button size="small" sx={{ minWidth: 0, p: 0, textTransform: 'none' }} onClick={() => onOpenVariant(score.variantId)}>
                            {score.variantName ?? score.variantId}
                          </Button>
                        ) : (
                          <span>{score.variantName ?? score.variantId}</span>
                        )}
                        {score.variantName && (
                          <Typography variant="caption" color="text.secondary">{score.variantId}</Typography>
                        )}
                        {score.role && !isControl && <Chip label={score.role} size="small" variant="outlined" />}
                        {isControl && <Chip label="control" size="small" />}
                        {isWinner && <Chip label="winner" size="small" color="success" />}
                      </Stack>
                    </TableCell>
                    <TableCell align="right">{score.assignedUsers}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      {PRIMARY_METRIC_VALUE[row.primaryMetric]?.(score) ?? 'n/a'}
                    </TableCell>
                    <TableCell align="right" data-testid={DECISION_SCORECARD_TEST_IDS.ciCell(score.variantId)}>
                      {isControl || !comparison ? (
                        <Typography variant="caption" color="text.secondary">—</Typography>
                      ) : (
                        <>
                          <Typography variant="caption" component="div">
                            {comparison.confidenceInterval95
                              ? `${comparison.delta >= 0 ? '+' : ''}${comparison.delta.toFixed(2)} [${comparison.confidenceInterval95[0].toFixed(2)}, ${comparison.confidenceInterval95[1].toFixed(2)}]`
                              : 'no interval'}
                          </Typography>
                          <CiBar interval={comparison.confidenceInterval95} domainMax={ciDomainMax} />
                        </>
                      )}
                    </TableCell>
                    <TableCell align="right" sx={{ color: probabilityColor(comparison?.probabilityToBeatControl ?? null), fontWeight: 600 }}>
                      {formatProbability(comparison?.probabilityToBeatControl ?? null)}
                    </TableCell>
                    <TableCell align="right">{formatMoney(comparison?.expectedLossPerUser ?? null)}</TableCell>
                    {secondaryColumns.map(([key]) => (
                      <TableCell key={key} align="right">{PRIMARY_METRIC_VALUE[key]?.(score) ?? 'n/a'}</TableCell>
                    ))}
                    <TableCell align="right">
                      {score.qualification ? <StatTierChip qualification={score.qualification} /> : '—'}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
            );
          })()}
        </Box>
      </Collapse>
    </Paper>
  );
};
